import { InMemoryCache } from '@apollo/client';
import zipcode_to_timezone from 'zipcode-to-timezone';
import dateFnsFormat from 'date-fns/format';
import { asBrowserDate, asDateInTZ, asUTCDate } from '@/utilities/convertToISO';
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';
import { subDays } from 'date-fns';

const instance = new InMemoryCache({
    typePolicies: {
        Query: {
            fields: {
                // Root orders lists (including the planning query's pickups/
                // dropoffs aliases) are keyed per-args and always written as
                // complete result sets, so replacement is intentional — declared
                // explicitly to silence the default-merge data-loss dev warning
                // when a cache patch shrinks a list (e.g. an order leaving the
                // unassigned bucket on assignment). Replace-only: do NOT build
                // cache-backed pagination on the root orders field (keyArgs
                // excluding offset/limit) without revisiting this policy —
                // fetchMore pages would silently replace instead of accumulate.
                orders: {
                    merge(existing = [], incoming = []) {
                        return incoming;
                    },
                },
            },
        },
        claims: {
            keyFields: ['claim_id'],
        },
        claim_items: {
            keyFields: ['claim_item_id'],
        },
        claim_adjustments: {
            keyFields: ['claim_adjustment_id'],
        },
        notes: {
            keyFields: ['note_id'],
        },
        notifications: {
            keyFields: ['notification_id'],
        },
        communications: {
            keyFields: ['communication_id'],
        },
        users: {
            keyFields: ['user_id'],
        },
        clients: {
            keyFields: ['client_id'],
        },
        automations: {
            keyFields: ['automation_id'],
        },
        overrides: {
            keyFields: ['override_id'],
        },
        shippers: {
            keyFields: ['shipper_id'],
        },
        carriers: {
            keyFields: ['carrier_id'],
        },
        routes: {
            keyFields: ['route_id'],
            fields: {
                stopsByRouteId: {
                    // Replace, don't union: the planning mutations are
                    // full-replace (delete_stops + insert_stops of the entire
                    // recomputed set) and every query selecting this field
                    // fetches the complete per-route stop list, so incoming is
                    // always authoritative. Unioning kept deleted stops
                    // attached to routes forever. The filter drops refs whose
                    // entity was evicted mid-update (route_id reads undefined).
                    merge(existing = [], incoming = [], { readField }) {
                        return incoming.filter((stopRef) => !!readField('route_id', stopRef));
                    },
                },
                orders: {
                    // Mapping lists are always written as complete sets (queries
                    // fetch every mapping per route; the assign cache patch
                    // rewrites the full list), so replacement is intentional —
                    // declared explicitly to silence the default-merge data-loss
                    // dev warning when an assignment shrinks the list.
                    merge(existing = [], incoming = []) {
                        return incoming;
                    },
                },
                scheduled_delivery_formatted: {
                    read(prev = null, { readField }) {
                        const [orders, deliveryDate] = ['orders', 'scheduled_delivery'].map((attr) =>
                            readField({ fieldName: attr })
                        );
                        if (!deliveryDate) {
                            return prev;
                        }

                        const order = orders?.length && readField('order', orders[0]);
                        const [orderType, pickupZip, dropoffZip] = ['order_type', 'pickup_zip', 'dropoff_zip'].map(
                            (attr) => order && readField(attr, order)
                        );
                        const zip = orderType === 'return' ? pickupZip : dropoffZip;
                        const tz = zipcode_to_timezone.lookup(zip) || 'America/New_York';

                        return formatInTimeZone(asDateInTZ(deliveryDate, tz), tz, 'MM/dd/yyyy');
                    },
                },
                completed_formatted: {
                    read(prev = null, { readField }) {
                        const [orders, completed] = ['orders', 'completed'].map((attr) =>
                            readField({ fieldName: attr })
                        );
                        if (!completed) {
                            return prev;
                        }

                        const order = orders?.length && readField('order', orders[0]);
                        const [orderType, pickupZip, dropoffZip] = ['order_type', 'pickup_zip', 'dropoff_zip'].map(
                            (attr) => order && readField(attr, order)
                        );
                        const zip = orderType === 'return' ? pickupZip : dropoffZip;
                        const tz = zipcode_to_timezone.lookup(zip) || 'America/New_York';

                        return formatInTimeZone(asDateInTZ(completed, tz), tz, 'MM/dd/yyyy');
                    },
                },
            },
        },
        orders: {
            keyFields: ['order_id'],
            fields: {
                returns: {
                    read(prev = [], { readField }) {
                        const items = readField({ fieldName: 'itemsByOrderId' });
                        if (!items || items.length === 0) {
                            return prev;
                        }

                        return items.filter((item) => readField('is_return', item));
                    },
                },
                items: {
                    read(prev = [], { readField }) {
                        const items = readField({ fieldName: 'itemsByOrderId' });
                        if (!items || items.length === 0) {
                            return prev;
                        }

                        return items.filter((item) => !readField('is_return', item));
                    },
                },
                first_available_date_formatted: {
                    read(prev = null, { readField }) {
                        const [orderType, deliveryDate, pickupZip, dropoffZip] = [
                            'order_type',
                            'first_available_date',
                            'pickup_zip',
                            'dropoff_zip',
                        ].map((attr) => readField({ fieldName: attr }));
                        if (!deliveryDate) {
                            return prev;
                        }

                        const zip = orderType === 'return' ? pickupZip : dropoffZip;
                        const tz = zipcode_to_timezone.lookup(zip) || 'America/New_York';

                        return formatInTimeZone(asDateInTZ(deliveryDate, tz), tz, 'EEE, MMM d, yyyy');
                    },
                },
                target_delivery_date_formatted: {
                    read(prev = null, { readField }) {
                        const [orderType, targetDeliveryDate, pickupZip, dropoffZip] = [
                            'order_type',
                            'target_delivery_date',
                            'pickup_zip',
                            'dropoff_zip',
                        ].map((attr) => readField({ fieldName: attr }));
                        if (!targetDeliveryDate) {
                            return prev;
                        }

                        const zip = orderType === 'return' ? pickupZip : dropoffZip;
                        const tz = zipcode_to_timezone.lookup(zip) || 'America/New_York';

                        return formatInTimeZone(asDateInTZ(targetDeliveryDate, tz), tz, 'EEE, MMM d, yyyy');
                    },
                },
                billing_completion_date_formatted: {
                    read(prev = null, { readField }) {
                        const [orderType, completionDate, pickupZip, dropoffZip] = [
                            'order_type',
                            'billing_completion_time',
                            'pickup_zip',
                            'dropoff_zip',
                        ].map((attr) => readField({ fieldName: attr }));
                        if (!completionDate) {
                            return prev;
                        }

                        const zip = orderType === 'return' ? pickupZip : dropoffZip;
                        const tz = zipcode_to_timezone.lookup(zip) || 'America/New_York';

                        return formatInTimeZone(asDateInTZ(completionDate, tz), tz, 'EEE, MMM d, yyyy');
                    },
                },
                delivery_date_formatted: {
                    read(prev = null, { readField }) {
                        const [orderType, deliveryDate, pickupZip, dropoffZip] = [
                            'order_type',
                            'delivery_date',
                            'pickup_zip',
                            'dropoff_zip',
                        ].map((attr) => readField({ fieldName: attr }));
                        if (!deliveryDate) {
                            return prev;
                        }

                        const zip = orderType === 'return' ? pickupZip : dropoffZip;
                        const tz = zipcode_to_timezone.lookup(zip) || 'America/New_York';

                        return formatInTimeZone(asDateInTZ(deliveryDate, tz), tz, 'EEE, MMM d, yyyy');
                    },
                },
                completion_date_formatted: {
                    read(prev = null, { readField }) {
                        const [orderType, completionDate, pickupZip, dropoffZip] = [
                            'order_type',
                            'completion_time',
                            'pickup_zip',
                            'dropoff_zip',
                        ].map((attr) => readField({ fieldName: attr }));
                        if (!completionDate) {
                            return prev;
                        }

                        const zip = orderType === 'return' ? pickupZip : dropoffZip;
                        const tz = zipcode_to_timezone.lookup(zip) || 'America/New_York';

                        return formatInTimeZone(asDateInTZ(completionDate, tz), tz, 'EEE, MMM d, yyyy');
                    },
                },
                pickup_date_formatted: {
                    read(prev = null, { readField }) {
                        const [orderType, pickupDate, pickupZip, dropoffZip] = [
                            'order_type',
                            'pickup_date',
                            'pickup_zip',
                            'dropoff_zip',
                        ].map((attr) => readField({ fieldName: attr }));
                        if (!pickupDate) {
                            return prev;
                        }

                        const zip = orderType === 'return' ? pickupZip : dropoffZip;
                        const tz = zipcode_to_timezone.lookup(zip) || 'America/New_York';

                        return formatInTimeZone(asDateInTZ(pickupDate, tz), tz, 'EEE, MMM d, yyyy');
                    },
                },
                estimated_delivery_range_start_formatted: {
                    read(prev = null, { readField }) {
                        const [orderType, date, pickupZip, dropoffZip] = [
                            'order_type',
                            'estimated_delivery_range_start',
                            'pickup_zip',
                            'dropoff_zip',
                        ].map((attr) => readField({ fieldName: attr }));
                        if (!date) {
                            return prev;
                        }

                        const zip = orderType === 'return' ? pickupZip : dropoffZip;
                        const tz = zipcode_to_timezone.lookup(zip) || 'America/New_York';

                        return formatInTimeZone(asDateInTZ(date, tz), tz, 'EEE, MMM d, yyyy');
                    },
                },
                estimated_delivery_range_end_formatted: {
                    read(prev = null, { readField }) {
                        const [orderType, date, pickupZip, dropoffZip] = [
                            'order_type',
                            'estimated_delivery_range_end',
                            'pickup_zip',
                            'dropoff_zip',
                        ].map((attr) => readField({ fieldName: attr }));
                        if (!date) {
                            return prev;
                        }

                        const zip = orderType === 'return' ? pickupZip : dropoffZip;
                        const tz = zipcode_to_timezone.lookup(zip) || 'America/New_York';

                        return formatInTimeZone(asDateInTZ(date, tz), tz, 'EEE, MMM d, yyyy');
                    },
                },
                preferred_delivery_date_formatted: {
                    read(prev = null, { readField }) {
                        const [orderType, prefDelDate, pickupZip, dropoffZip] = [
                            'order_type',
                            'preferred_delivery_date',
                            'pickup_zip',
                            'dropoff_zip',
                        ].map((attr) => readField({ fieldName: attr }));
                        if (!prefDelDate) {
                            return prev;
                        }

                        const zip = orderType === 'return' ? pickupZip : dropoffZip;
                        const tz = zipcode_to_timezone.lookup(zip) || 'America/New_York';

                        return formatInTimeZone(asDateInTZ(prefDelDate, tz), tz, 'EEE, MMM d, yyyy');
                    },
                },
                alternative_delivery_dates_formatted: {
                    read(prev = [], { readField }) {
                        const [orderType, altDelDates, pickupZip, dropoffZip] = [
                            'order_type',
                            'alternative_delivery_dates',
                            'pickup_zip',
                            'dropoff_zip',
                        ].map((attr) => readField({ fieldName: attr }));
                        if (!altDelDates) {
                            return prev;
                        }

                        const zip = orderType === 'return' ? pickupZip : dropoffZip;
                        const tz = zipcode_to_timezone.lookup(zip) || 'America/New_York';

                        const formatted = altDelDates.map((date) =>
                            formatInTimeZone(asDateInTZ(date, tz), tz, 'EEE, MMM d, yyyy')
                        );

                        return formatted;
                    },
                },
                order_exception: {
                    read(prev = null, { readField }) {
                        const exceptions = readField({ fieldName: 'exceptions' });
                        if (exceptions.length === 0) {
                            return prev;
                        }

                        const unresolved = exceptions
                            .filter((mapping) => {
                                return (
                                    readField('status', mapping.exception) !== 'RESOLVED' &&
                                    mapping.exception_type === 'ORDER'
                                );
                            })
                            .sort((l, r) => {
                                return new Date(r.created_at).getTime() - new Date(l.created_at).getTime();
                            });

                        return unresolved.length > 0 ? unresolved[0].exception : null;
                    },
                },
                external_integration_id: {
                    read(prev = null, { readField }) {
                        const extAttr = readField({ fieldName: 'external_integration_attr' });

                        if (!extAttr) {
                            return null;
                        }

                        // Coalesce to null when the dynamically-named source column
                        // isn't cached for this order. Returning undefined from a read
                        // policy marks this @client field — and therefore the entire
                        // query read — incomplete. That silently defeats
                        // cache.updateQuery-based optimistic patches (e.g. the plan
                        // page assign-to-route flow, which reads QUERY_PLANNING_DATA
                        // from cache) and forces redundant network refetches.
                        const value = readField({ fieldName: extAttr });
                        return value === undefined ? null : value;
                    },
                },
            },
        },
        // order_wh_transitions: {
        //     keyFields: ['action'],
        // },
        order_wh_events: {
            keyFields: ['event_id'],
            fields: Object.fromEntries(
                [
                    'est_pickup_date',
                    'est_received_date',
                    'est_ship_date',
                    'pickup_date',
                    'received_date',
                    'ship_date',
                ].map((attr) => [
                    attr,
                    {
                        read(prev) {
                            if (!prev) {
                                return null;
                            }

                            return asUTCDate(prev).toISOString();
                        },
                    },
                ])
            ),
        },
        items: {
            keyFields: ['item_id'],
            fields: {
                item_exception: {
                    read(prev = null, { readField }) {
                        const exceptions = readField({ fieldName: 'exceptions' });
                        if (!exceptions || exceptions.length === 0) {
                            return prev;
                        }

                        const unresolved = exceptions
                            .filter((mapping) => {
                                return (
                                    readField('status', mapping.exception) !== 'RESOLVED' &&
                                    mapping.exception_type === 'ITEM'
                                );
                            })
                            .sort((l, r) => {
                                return new Date(r.created_at).getTime() - new Date(l.created_at).getTime();
                            });

                        return unresolved.length > 0 ? unresolved[0].exception : null;
                    },
                },
            },
        },
        teammates: {
            keyFields: ['teammate_id'],
        },
        staging_verifications: {
            keyFields: ['staging_verification_id'],
        },
        ratings: {
            keyFields: ['rating_id'],
        },
        stops: {
            keyFields: ['stop_id'],
        },
        messages: {
            keyFields: ['message_id'],
            fields: {
                timestamp: {
                    read(_, { readField }) {
                        return (
                            readField({ fieldName: 'scheduled_timestamp' }) || readField({ fieldName: 'created_at' })
                        );
                    },
                },
            },
        },
        locations: {
            keyFields: ['location_id'],
        },
        exceptions: {
            keyFields: ['exception_id'],
        },
        invoices: {
            keyFields: ['invoice_id'],
        },
        jobs: {
            keyFields: ['job_id'],
        },
        activity_orders: {
            keyFields: ['order_id'],
        },
        activity_routes: {
            keyFields: ['route_id'],
        },
        activity_stops: {
            keyFields: ['stop_id'],
        },
        listings: {
            keyFields: ['listing_id'],
            fields: {
                expiration_date_formatted: {
                    read(_, { readField }) {
                        return dateFnsFormat(
                            asBrowserDate(
                                subDays(new Date(readField({ fieldName: 'expiration_date' })), 1).toISOString()
                            ),
                            'EEE, MMM d, yyyy'
                        );
                    },
                },
            },
        },
        bids: {
            keyFields: ['bid_id'],
            fields: {
                expiration_date_formatted: {
                    read(_, { readField }) {
                        return dateFnsFormat(
                            asBrowserDate(
                                subDays(new Date(readField({ fieldName: 'expiration_date' })), 1).toISOString()
                            ),
                            'EEE, MMM d, yyyy'
                        );
                    },
                },
            },
        },
        partnerships: {
            keyFields: ['partnership_id'],
        },
        subregions: {
            keyFields: ['subregion_id'],
        },
        subregion_zip_mappings: {
            keyFields: ['mapping_id'],
        },
        subregion_carrier_mappings: {
            keyFields: ['mapping_id'],
        },
        shipping_partners: {
            keyFields: ['shipping_partner_id'],
        },
        resources: {
            keyFields: ['resource_id'],
            fields: {
                zips: {
                    read(prev = [], { readField }) {
                        const [subregions] = ['subregions'].map((attr) => readField({ fieldName: attr }));
                        if (!subregions) {
                            return [];
                        }

                        return subregions.reduce((acc, subregionRef) => {
                            const subregion = readField('subregion', subregionRef);
                            const zips = readField('zips', subregion);
                            acc = [
                                ...acc,
                                ...(zips || []).map((zipRef) => {
                                    const zip = readField('zip', zipRef);

                                    return zip;
                                }),
                            ];
                            return acc;
                        }, []);
                    },
                },
            },
        },
        resource_region_mappings: {
            keyFields: ['mapping_id'],
        },
        resource_rule_mappings: {
            keyFields: ['mapping_id'],
            fields: Object.fromEntries(
                ['override_end', 'override_start'].map((attr) => [
                    attr,
                    {
                        read(prev) {
                            if (!prev) {
                                return null;
                            }

                            return asUTCDate(prev).toISOString();
                        },
                    },
                ])
            ),
        },
        resource_override_mappings: {
            keyFields: ['mapping_id'],
            fields: Object.fromEntries(
                ['rule_end', 'rule_start'].map((attr) => [
                    attr,
                    {
                        read(prev) {
                            if (!prev) {
                                return null;
                            }

                            return asUTCDate(prev).toISOString();
                        },
                    },
                ])
            ),
        },
        carrier_invoices: {
            keyFields: ['carrier_invoice_id'],
            fields: {
                shipper_orders: { merge: false },
                orders: { merge: false },
                middle_mile_carrier_orders: { merge: false },
                warehouse_carrier_orders: { merge: false },
                contractor_orders: { merge: false },
            },
        },
        driver_tariff_mappings: {
            keyFields: ['mapping_id'],
        },
        driver_payments: {
            keyFields: ['payment_id'],
        },
        pricing_overrides: {
            keyFields: ['pricing_override_id'],
        },
        pricing_tariffs: {
            keyFields: ['tariff_id'],
        },
        manifests: {
            keyFields: ['manifest_id'],
        },
        manifests_inbound_items: {
            keyFields: ['mapping_id'],
        },
        pallet_items: {
            keyFields: ['pallet_id'],
        },
        pallet_item_logs: {
            keyFields: ['log_id'],
        },
        exception_mapping: {
            keyFields: ['mapping_id'],
        },
        resource_shipping_partner_mappings: {
            keyFields: ['mapping_id'],
        },
        order_route_mappings: {
            keyFields: ['mapping_id'],
        },
        programs: {
            keyFields: ['program_id'],
        },
        service_levels: {
            keyFields: ['service_level_id'],
        },
        services: {
            keyFields: ['service_id'],
        },
        survey_questions: {
            keyFields: ['survey_id'],
        },
        survey_responses: {
            keyFields: ['response_id'],
        },
        tags: {
            keyFields: ['tag_id'],
        },
        integrations: {
            keyFields: ['transaction_id'],
        },
        integration_errors: {
            keyFields: ['error_id'],
        },
        drivers: {
            keyFields: ['driver_id'],
        },
        driver_tracking: {
            keyFields: ['driver_id', 'time'],
        },
        order_activity_logs: {
            keyFields: ['log_id'],
        },
        integration_meta_types: {
            keyFields: ['integration_type_id'],
        },
        integration_meta_pricing_overrides: {
            keyFields: ['integration_pricing_override_id'],
        },
        integration_meta_integration_overrides: {
            keyFields: ['integration_integration_overrides_id'],
        },
        permissions: {
            keyFields: ['permission_id'],
        },
        pricing_runs: {
            keyFields: ['run_id'],
        },
        pricing_run_instances: {
            keyFields: ['instance_id'],
        },
        item_dimensions: {
            keyFields: ['item_dimension_id'],
        },
        item_dimension_documents: {
            keyFields: ['item_dimension_document_id'],
        },
        delivery_profiles: {
            keyFields: ['delivery_profile_id'],
        },
        tracking_events: {
            keyFields: ['event_id'],
        },
        email_order_subscriptions: {
            keyFields: ['subscription_id'],
        },
        client_dispatch_settings: {
            keyFields: ['dispatch_settings_id'],
        },
    },
});

export default instance;
